import { db } from "@/lib/firebase";
import type { SearchIntent } from "@/lib/search/aiQueryIntent";
import { normalizeText } from "@/lib/search/businessSearch";
import {
  buildDirectorySearchExpansion,
  getSearchCandidateAliases,
  type DirectorySearchExpansion,
} from "@/lib/search/searchExpansion";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Query,
  type QueryConstraint,
  type Unsubscribe,
} from "firebase/firestore";

const DIRECTORY_TEXT_SEPARATOR = " | ";
const QUERY_PREFIX_LIMIT = 10;
const MAX_EXPANDED_RANKING_TERMS = 24;
const SEARCH_FETCH_MULTIPLIER = 4;
const MIN_SEARCH_FETCH_LIMIT = 120;

const SEARCH_HELPER_WORDS = new Set([
  "a",
  "an",
  "and",
  "around",
  "at",
  "by",
  "for",
  "from",
  "in",
  "near",
  "nearby",
  "now",
  "of",
  "on",
  "open",
  "or",
  "the",
  "to",
  "with",
]);

export type DirectoryBusinessStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

export type DirectoryBusiness = {
  id: string;
  businessId: string;
  slug?: string | null;

  businessName?: string;
  businessNameLower?: string;

  categoryId?: string | null;
  categoryName?: string | null;
  categoryNameLower?: string;

  subcategoryId?: string | null;
  subcategoryName?: string | null;
  subcategoryNameLower?: string;

  countyId?: string | null;
  countyName?: string | null;

  cityId?: string | null;
  cityName?: string | null;

  areaId?: string | null;
  areaName?: string | null;

  shortDescription?: string | null;

  logoUrl?: string | null;
  coverUrl?: string | null;
  phone?: string | null;
  whatsapp?: string | null;

  isVerified?: boolean;
  isFeatured?: boolean;
  featuredRank?: number | null;
  isOpenNow?: boolean;

  status?: DirectoryBusinessStatus;
  isDeleted?: boolean;
  isPublished?: boolean;
  isDeactivated?: boolean;

  searchWords?: string[];
  searchTokens?: string[];
  searchPrefixes?: string[];
  searchText?: string;
  tokens?: string[];
  phraseTokens?: string[];
  itemTokens?: string[];
  locationTokens?: string[];
  categoryTokens?: string[];
  searchableText?: string;
  itemCount?: number;
  hasLogo?: boolean;
  hasProfileImage?: boolean;
  hasLocationPin?: boolean;
  hasWhatsApp?: boolean;

  name?: string;
  category?: string;
  categories?: string[];
  subCategory?: string | string[];
  addressLine?: string;
  cityLine?: string;
};

type SearchOptions = {
  search?: string;
  intent?: SearchIntent | null;
  cityId?: string | null;
  areaId?: string | null;
  limitCount?: number;
};

function normalizeSearch(value: string) {
  return normalizeText(value || "");
}

function uniqueSearchStrings(values: string[], maxCount = Number.POSITIVE_INFINITY) {
  const seen = new Set<string>();
  const output: string[] = [];

  for (const value of values) {
    const clean = normalizeSearch(value);
    if (!clean || seen.has(clean)) continue;

    seen.add(clean);
    output.push(clean);

    if (output.length >= maxCount) break;
  }

  return output;
}

function tokenizePhrase(value: string): string[] {
  const normalized = normalizeSearch(value);
  if (!normalized) return [];

  return normalized.split(" ").filter(Boolean);
}

function tokenizeSearchTerms(value: string): string[] {
  return uniqueSearchStrings(
    tokenizePhrase(value).filter((token) => !SEARCH_HELPER_WORDS.has(token))
  );
}

function getSearchAliases(value: string): string[] {
  const clean = normalizeSearch(value);
  if (!clean) return [];

  return getSearchCandidateAliases(clean);
}

function buildExpandedWordSet(values: string[]): Set<string> {
  return new Set(
    values.flatMap((value) => {
      const clean = normalizeSearch(value);
      if (!clean) return [];

      return [
        clean,
        ...tokenizePhrase(clean).flatMap((token) => getSearchAliases(token)),
      ];
    })
  );
}

function tokenMatchesWordSet(wordSet: Set<string>, token: string): boolean {
  return getSearchAliases(token).some((alias) => wordSet.has(alias));
}

function termMatchesWordSet(wordSet: Set<string>, term: string): boolean {
  return getSearchAliases(term).some((alias) => {
    if (wordSet.has(alias)) return true;

    const tokens = tokenizeSearchTerms(alias);
    return (
      tokens.length > 0 &&
      tokens.every((token) => tokenMatchesWordSet(wordSet, token))
    );
  });
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => String(item ?? "").trim())
    .filter(Boolean);
}

function mapDoc(
  data: Record<string, unknown>,
  docId: string
): DirectoryBusiness {
  const businessId =
    typeof data.businessId === "string" && data.businessId.trim()
      ? data.businessId
      : docId;

  const businessName = asString(data.businessName, "");
  const categoryName = asNullableString(data.categoryName);
  const subcategoryName = asNullableString(data.subcategoryName);
  const countyName = asNullableString(data.countyName);
  const cityName = asNullableString(data.cityName);
  const areaName = asNullableString(data.areaName);

  return {
    id: businessId,
    businessId,
    slug: asNullableString(data.slug),

    businessName,
    businessNameLower: asString(data.businessNameLower, ""),

    categoryId: asNullableString(data.categoryId),
    categoryName,
    categoryNameLower: asString(data.categoryNameLower, ""),

    subcategoryId: asNullableString(data.subcategoryId),
    subcategoryName,
    subcategoryNameLower: asString(data.subcategoryNameLower, ""),

    countyId: asNullableString(data.countyId),
    countyName,

    cityId: asNullableString(data.cityId),
    cityName,

    areaId: asNullableString(data.areaId),
    areaName,

    shortDescription: asNullableString(data.shortDescription),

    logoUrl: asNullableString(data.logoUrl),
    coverUrl: asNullableString(data.coverUrl),
    phone: asNullableString(data.phone),
    whatsapp: asNullableString(data.whatsapp),

    isVerified: asBoolean(data.isVerified),
    isFeatured: asBoolean(data.isFeatured),
    featuredRank: asNumber(data.featuredRank),
    isOpenNow:
      typeof data.isOpenNow === "boolean" ? data.isOpenNow : undefined,

    status: (data.status as DirectoryBusinessStatus | undefined) ?? "draft",
    isDeleted: asBoolean(data.isDeleted),
    isPublished: asBoolean(data.isPublished),
    isDeactivated: asBoolean(data.isDeactivated),

    searchWords: asStringArray(data.searchWords),
    searchTokens: asStringArray(data.searchTokens),
    searchPrefixes: asStringArray(data.searchPrefixes),
    searchText: asString(data.searchText, ""),
    tokens: asStringArray(data.tokens),
    phraseTokens: asStringArray(data.phraseTokens),
    itemTokens: asStringArray(data.itemTokens),
    locationTokens: asStringArray(data.locationTokens),
    categoryTokens: asStringArray(data.categoryTokens),
    searchableText: asString(data.searchableText, ""),
    itemCount: asNumber(data.itemCount) ?? undefined,
    hasLogo: asBoolean(data.hasLogo),
    hasProfileImage: asBoolean(data.hasProfileImage),
    hasLocationPin: asBoolean(data.hasLocationPin),
    hasWhatsApp: asBoolean(data.hasWhatsApp),

    name: businessName,
    category: categoryName ?? "",
    categories: categoryName ? [categoryName] : [],
    subCategory: subcategoryName ? [subcategoryName] : [],
    addressLine: areaName ?? "",
    cityLine: [cityName, countyName].filter(Boolean).join(DIRECTORY_TEXT_SEPARATOR),
  };
}

function buildHaystack(business: DirectoryBusiness): string {
  return [
    business.businessNameLower ||
      normalizeSearch(business.businessName || business.name || ""),
    business.categoryNameLower ||
      normalizeSearch(business.categoryName || business.category || ""),
    business.subcategoryNameLower ||
      normalizeSearch(business.subcategoryName || ""),
    normalizeSearch(business.countyName || ""),
    normalizeSearch(business.cityName || ""),
    normalizeSearch(business.areaName || ""),
    normalizeSearch(business.shortDescription || ""),
    normalizeSearch(business.searchText || ""),
    normalizeSearch(business.searchableText || ""),
    ...(business.tokens ?? []).map((word) => normalizeSearch(word)),
    ...(business.searchWords ?? []).map((word) => normalizeSearch(word)),
    ...(business.searchTokens ?? []).map((word) => normalizeSearch(word)),
    ...(business.phraseTokens ?? []).map((word) => normalizeSearch(word)),
    ...(business.itemTokens ?? []).map((word) => normalizeSearch(word)),
    ...(business.locationTokens ?? []).map((word) => normalizeSearch(word)),
    ...(business.categoryTokens ?? []).map((word) => normalizeSearch(word)),
  ]
    .filter(Boolean)
    .join(" ");
}

function buildHaystackWordSet(business: DirectoryBusiness): Set<string> {
  return buildExpandedWordSet(tokenizePhrase(buildHaystack(business)));
}

function normalizeIntentTerms(values: string[] | undefined): string[] {
  const seen = new Set<string>();
  const output: string[] = [];

  for (const value of values ?? []) {
    const clean = normalizeSearch(value);
    if (!clean || seen.has(clean)) continue;

    seen.add(clean);
    output.push(clean);
  }

  return output;
}

function setMatchesTerm(values: Set<string>, term: string): boolean {
  return termMatchesWordSet(values, term);
}

function textMatchesTerm(
  text: string,
  textWords: Set<string>,
  term: string
): boolean {
  if (!term) return false;

  for (const alias of getSearchAliases(term)) {
    if (text.includes(alias)) return true;
    if (termMatchesWordSet(textWords, alias)) return true;
  }

  return false;
}

function scoreIntentTerm(options: {
  term: string;
  primaryText?: string;
  primaryWords?: Set<string>;
  scopedTokens?: Set<string>;
  haystack: string;
  haystackWords: Set<string>;
  primaryScore: number;
  scopedScore: number;
  fallbackScore: number;
}): number {
  const term = normalizeSearch(options.term);
  if (!term) return 0;

  if (
    options.primaryText &&
    textMatchesTerm(
      options.primaryText,
      options.primaryWords ?? new Set(tokenizePhrase(options.primaryText)),
      term
    )
  ) {
    return options.primaryScore;
  }

  if (options.scopedTokens && setMatchesTerm(options.scopedTokens, term)) {
    return options.scopedScore;
  }

  if (textMatchesTerm(options.haystack, options.haystackWords, term)) {
    return options.fallbackScore;
  }

  return 0;
}

function scoreBusinessIntent(
  business: DirectoryBusiness,
  intent: SearchIntent | null | undefined
): number {
  if (!intent) return 0;

  const name = normalizeSearch(business.businessName || business.name || "");
  const category = normalizeSearch(
    business.categoryName || business.category || ""
  );
  const subcategory = normalizeSearch(business.subcategoryName || "");
  const county = normalizeSearch(business.countyName || "");
  const city = normalizeSearch(business.cityName || "");
  const area = normalizeSearch(business.areaName || "");
  const description = normalizeSearch(business.shortDescription || "");
  const searchText = normalizeSearch(business.searchText || "");
  const searchableText = normalizeSearch(business.searchableText || "");
  const haystack = buildHaystack(business);
  const haystackWords = buildHaystackWordSet(business);
  const categoryText = [category, subcategory].filter(Boolean).join(" ");
  const locationText = [area, city, county].filter(Boolean).join(" ");
  const itemServiceText = [description, searchText, searchableText]
    .filter(Boolean)
    .join(" ");

  const searchWords = buildExpandedWordSet(
    [
      ...(business.tokens ?? []),
      ...(business.searchWords ?? []),
      ...(business.searchTokens ?? []),
      ...(business.phraseTokens ?? []),
      ...(business.itemTokens ?? []),
      ...(business.locationTokens ?? []),
      ...(business.categoryTokens ?? []),
    ].map((word) => normalizeSearch(word))
  );
  const phraseTokens = buildExpandedWordSet(business.phraseTokens ?? []);
  const itemTokens = buildExpandedWordSet(business.itemTokens ?? []);
  const locationTokens = buildExpandedWordSet(business.locationTokens ?? []);
  const categoryTokens = buildExpandedWordSet(business.categoryTokens ?? []);
  const nameWords = buildExpandedWordSet(tokenizePhrase(name));
  const categoryWords = buildExpandedWordSet(tokenizePhrase(categoryText));
  const locationWords = buildExpandedWordSet(tokenizePhrase(locationText));
  const itemServiceWords = buildExpandedWordSet(
    tokenizePhrase(itemServiceText)
  );

  let score = 0;

  for (const term of normalizeIntentTerms(intent.mustHave)) {
    const primaryScore = scoreIntentTerm({
      term,
      primaryText: [name, categoryText, itemServiceText].filter(Boolean).join(" "),
      primaryWords: new Set([
        ...nameWords,
        ...categoryWords,
        ...itemServiceWords,
      ]),
      scopedTokens: searchWords,
      haystack,
      haystackWords,
      primaryScore: 72,
      scopedScore: 54,
      fallbackScore: 28,
    });

    score += primaryScore || -12;
  }

  for (const term of normalizeIntentTerms(intent.itemHints)) {
    score += scoreIntentTerm({
      term,
      primaryText: itemServiceText,
      primaryWords: itemServiceWords,
      scopedTokens: itemTokens,
      haystack,
      haystackWords,
      primaryScore: 100,
      scopedScore: 110,
      fallbackScore: 28,
    });
  }

  for (const term of normalizeIntentTerms(intent.serviceHints)) {
    score += scoreIntentTerm({
      term,
      primaryText: itemServiceText,
      primaryWords: itemServiceWords,
      scopedTokens: itemTokens,
      haystack,
      haystackWords,
      primaryScore: 82,
      scopedScore: 88,
      fallbackScore: 24,
    });
  }

  for (const term of normalizeIntentTerms(intent.categoryHints)) {
    score += scoreIntentTerm({
      term,
      primaryText: categoryText,
      primaryWords: categoryWords,
      scopedTokens: categoryTokens,
      haystack,
      haystackWords,
      primaryScore: 96,
      scopedScore: 104,
      fallbackScore: 24,
    });
  }

  for (const term of normalizeIntentTerms(intent.locationHints)) {
    score += scoreIntentTerm({
      term,
      primaryText: locationText,
      primaryWords: locationWords,
      scopedTokens: locationTokens,
      haystack,
      haystackWords,
      primaryScore: 90,
      scopedScore: 96,
      fallbackScore: 18,
    });
  }

  if (intent.nearLocation) {
    score += scoreIntentTerm({
      term: intent.nearLocation,
      primaryText: locationText,
      primaryWords: locationWords,
      scopedTokens: locationTokens,
      haystack,
      haystackWords,
      primaryScore: 130,
      scopedScore: 120,
      fallbackScore: 20,
    });
  }

  for (const term of normalizeIntentTerms(intent.phraseTokens)) {
    score += scoreIntentTerm({
      term,
      scopedTokens: phraseTokens,
      haystack,
      haystackWords,
      primaryScore: 0,
      scopedScore: 44,
      fallbackScore: 18,
    });
  }

  for (const term of normalizeIntentTerms(intent.tokens)) {
    if (tokenMatchesWordSet(searchWords, term)) score += 10;
    else if (textMatchesTerm(haystack, haystackWords, term)) score += 4;
  }

  if (intent.openNow || intent.timeIntent === "open_now") {
    if (business.isOpenNow === true) {
      score += 90;
    } else if (business.isOpenNow === false) {
      score -= 12;
    }
  }

  const confidence = Number.isFinite(intent.confidence)
    ? Math.max(0.5, Math.min(1, intent.confidence))
    : 0.75;

  return Math.round(score * confidence);
}

function matchesAllTokens(business: DirectoryBusiness, rawSearch: string) {
  const tokens = tokenizeSearchTerms(rawSearch);
  if (!tokens.length) return true;

  const haystackWords = buildHaystackWordSet(business);

  return tokens.every((token) => tokenMatchesWordSet(haystackWords, token));
}

function matchesExpandedCandidate(
  business: DirectoryBusiness,
  expansion: DirectorySearchExpansion
): boolean {
  if (expansion.candidateTerms.length === 0) return false;

  const haystack = buildHaystack(business);
  const haystackWords = buildHaystackWordSet(business);

  return expansion.candidateTerms.some((term) =>
    textMatchesTerm(haystack, haystackWords, term)
  );
}

function buildIntentFallbackTerms(
  intent: SearchIntent,
  includeLocationTerms: boolean
): string[] {
  const values = [
    intent.cleanQuery,
    ...intent.mustHave,
    ...intent.itemHints,
    ...intent.serviceHints,
    ...intent.categoryHints,
    ...intent.phraseTokens,
    ...intent.tokens,
  ];

  if (includeLocationTerms) {
    values.push(...intent.locationHints);
    if (intent.nearLocation) values.push(intent.nearLocation);
  }

  return uniqueSearchStrings(values);
}

function matchesAnyIntentTerm(
  business: DirectoryBusiness,
  intent: SearchIntent
): boolean {
  const haystack = buildHaystack(business);
  const haystackWords = buildHaystackWordSet(business);
  const primaryTerms = buildIntentFallbackTerms(intent, false);

  if (primaryTerms.some((term) => textMatchesTerm(haystack, haystackWords, term))) {
    return true;
  }

  if (primaryTerms.length > 0) return false;

  return buildIntentFallbackTerms(intent, true).some((term) =>
    textMatchesTerm(haystack, haystackWords, term)
  );
}

function dedupeDirectoryBusinesses(items: DirectoryBusiness[]): DirectoryBusiness[] {
  const seen = new Set<string>();
  const output: DirectoryBusiness[] = [];

  for (const item of items) {
    const key = item.businessId || item.id;
    if (!key || seen.has(key)) continue;

    seen.add(key);
    output.push(item);
  }

  return output;
}

function sumTopScores(scores: number[], maxCount: number): number {
  return scores
    .filter((score) => score > 0)
    .sort((a, b) => b - a)
    .slice(0, maxCount)
    .reduce((total, score) => total + score, 0);
}

function scoreBusiness(
  business: DirectoryBusiness,
  q: string,
  intent?: SearchIntent | null,
  expansion?: DirectorySearchExpansion
) {
  const tokens = tokenizeSearchTerms(q);

  const name = normalizeSearch(business.businessName || business.name || "");
  const category = normalizeSearch(
    business.categoryName || business.category || ""
  );
  const subcategory = normalizeSearch(business.subcategoryName || "");
  const county = normalizeSearch(business.countyName || "");
  const city = normalizeSearch(business.cityName || "");
  const area = normalizeSearch(business.areaName || "");
  const description = normalizeSearch(business.shortDescription || "");
  const searchText = normalizeSearch(business.searchText || "");
  const searchableText = normalizeSearch(business.searchableText || "");
  const searchWords = buildExpandedWordSet(
    [
      ...(business.tokens ?? []),
      ...(business.searchWords ?? []),
      ...(business.searchTokens ?? []),
      ...(business.phraseTokens ?? []),
      ...(business.itemTokens ?? []),
      ...(business.locationTokens ?? []),
      ...(business.categoryTokens ?? []),
    ].map((word) => normalizeSearch(word))
  );
  const phraseTokens = buildExpandedWordSet(business.phraseTokens ?? []);
  const itemTokens = buildExpandedWordSet(business.itemTokens ?? []);
  const locationTokens = buildExpandedWordSet(business.locationTokens ?? []);
  const categoryTokens = buildExpandedWordSet(business.categoryTokens ?? []);
  const categoryText = [category, subcategory].filter(Boolean).join(" ");
  const locationText = [area, city, county].filter(Boolean).join(" ");
  const itemServiceText = [description, searchText, searchableText]
    .filter(Boolean)
    .join(" ");
  const haystack = buildHaystack(business);
  const haystackWords = buildHaystackWordSet(business);
  const nameWords = buildExpandedWordSet(tokenizePhrase(name));
  const categoryWords = buildExpandedWordSet(tokenizePhrase(categoryText));
  const locationWords = buildExpandedWordSet(tokenizePhrase(locationText));
  const itemServiceWords = buildExpandedWordSet(tokenizePhrase(itemServiceText));
  const descriptionWords = buildExpandedWordSet(tokenizePhrase(description));

  let score = 0;

  if (name === q) score += 220;
  else if (name.startsWith(q)) score += 140;
  else if (name.includes(q)) score += 100;

  if (category === q) score += 60;
  else if (category.startsWith(q)) score += 36;
  else if (category.includes(q)) score += 24;

  if (subcategory === q) score += 60;
  else if (subcategory.startsWith(q)) score += 34;
  else if (subcategory.includes(q)) score += 22;

  if (area === q) score += 32;
  else if (area.includes(q)) score += 18;

  if (city === q) score += 28;
  else if (city.includes(q)) score += 16;

  if (county === q) score += 22;
  else if (county.includes(q)) score += 12;

  if (setMatchesTerm(itemTokens, q)) score += 160;
  if (setMatchesTerm(phraseTokens, q)) score += 110;
  if (searchText.includes(q)) score += 55;
  if (searchableText.includes(q)) score += 40;
  if (description.includes(q)) score += 35;
  if (setMatchesTerm(categoryTokens, q)) score += 70;
  if (setMatchesTerm(locationTokens, q)) score += 28;

  for (const token of tokens) {
    if (tokenMatchesWordSet(searchWords, token)) score += 20;
    if (tokenMatchesWordSet(itemTokens, token)) score += 12;
    if (tokenMatchesWordSet(categoryTokens, token)) score += 8;
    if (tokenMatchesWordSet(locationTokens, token)) score += 6;
    if (name.startsWith(token)) score += 12;
    if (name.includes(token)) score += 8;
    if (category.includes(token)) score += 6;
    if (subcategory.includes(token)) score += 6;
    if (city.includes(token) || area.includes(token) || county.includes(token)) {
      score += 5;
    }
  }

  if (expansion) {
    const scoreExpandedTerm = (
      term: string,
      scores: {
        itemToken: number;
        itemText: number;
        name: number;
        category: number;
        description: number;
        fallback: number;
      }
    ) => {
      const clean = normalizeSearch(term);
      if (!clean) return 0;

      if (setMatchesTerm(itemTokens, clean)) return scores.itemToken;
      if (textMatchesTerm(itemServiceText, itemServiceWords, clean)) {
        return scores.itemText;
      }
      if (textMatchesTerm(name, nameWords, clean)) return scores.name;
      if (textMatchesTerm(categoryText, categoryWords, clean)) {
        return scores.category;
      }
      if (textMatchesTerm(description, descriptionWords, clean)) {
        return scores.description;
      }
      if (textMatchesTerm(haystack, haystackWords, clean)) {
        return scores.fallback;
      }

      return 0;
    };

    const phraseScores = expansion.phraseAliases
      .slice(0, MAX_EXPANDED_RANKING_TERMS)
      .map((term) =>
        scoreExpandedTerm(term, {
          itemToken: 150,
          itemText: 70,
          name: 105,
          category: 90,
          description: 55,
          fallback: 24,
        })
      );
    const candidateScores = expansion.candidateTerms
      .slice(0, MAX_EXPANDED_RANKING_TERMS)
      .map((term) =>
        scoreExpandedTerm(term, {
          itemToken: 125,
          itemText: 58,
          name: 90,
          category: 76,
          description: 42,
          fallback: 18,
        })
      );
    const boostScores = expansion.boostTerms.slice(0, 12).map((term) =>
      scoreExpandedTerm(term, {
        itemToken: 18,
        itemText: 12,
        name: 10,
        category: 8,
        description: 5,
        fallback: 3,
      })
    );
    const locationScores = expansion.locationTerms.slice(0, 12).map((term) =>
      scoreIntentTerm({
        term,
        primaryText: locationText,
        primaryWords: locationWords,
        scopedTokens: locationTokens,
        haystack,
        haystackWords,
        primaryScore: 190,
        scopedScore: 200,
        fallbackScore: 8,
      })
    );
    const locationAliasScores = expansion.locationAliases.slice(0, 8).map((term) =>
      scoreIntentTerm({
        term,
        primaryText: locationText,
        primaryWords: locationWords,
        scopedTokens: locationTokens,
        haystack,
        haystackWords,
        primaryScore: 16,
        scopedScore: 18,
        fallbackScore: 4,
      })
    );

    score += sumTopScores(phraseScores, 4);
    score += sumTopScores(candidateScores, 6);
    score += sumTopScores(boostScores, 4);
    score += sumTopScores(locationScores, 3);
    score += sumTopScores(locationAliasScores, 2);
  }

  if (business.isFeatured) score += 4;
  if (business.isVerified) score += 2;
  if (business.hasLogo) score += 1;
  if (business.hasProfileImage) score += 1;
  if (business.hasLocationPin) score += 1;
  if (business.hasWhatsApp) score += 1;
  if (business.isFeatured && typeof business.featuredRank === "number") {
    score += Math.max(0, 8 - Math.min(business.featuredRank, 8));
  }

  return score + scoreBusinessIntent(business, intent);
}

type RankedDirectoryResults = {
  items: DirectoryBusiness[];
  candidateCount: number;
};

function rankResults(
  items: DirectoryBusiness[],
  rawSearch: string,
  intent?: SearchIntent | null,
  expansion?: DirectorySearchExpansion
): RankedDirectoryResults {
  const q = normalizeSearch(rawSearch);

  if (!q) return { items, candidateCount: items.length };

  const activeExpansion =
    expansion ?? buildDirectorySearchExpansion(rawSearch, intent);
  const strictMatches = items.filter((item) => matchesAllTokens(item, q));
  const expandedMatches = items.filter((item) =>
    matchesExpandedCandidate(item, activeExpansion)
  );
  const intentMatches = intent
    ? items.filter((item) => matchesAnyIntentTerm(item, intent))
    : [];
  const candidateItems = dedupeDirectoryBusinesses([
    ...strictMatches,
    ...expandedMatches,
    ...intentMatches,
  ]);

  const scoredItems = candidateItems.map((item) => ({
    item,
    score: scoreBusiness(item, q, intent, activeExpansion),
  }));

  const rankedItems = scoredItems
    .sort((a, b) => {
      const diff = b.score - a.score;

      if (diff !== 0) return diff;

      return (a.item.businessName || a.item.name || "").localeCompare(
        b.item.businessName || b.item.name || ""
      );
    })
    .map((row) => row.item);

  return {
    items: rankedItems,
    candidateCount: candidateItems.length,
  };
}

function getTopRankingReasons(
  business: DirectoryBusiness,
  rawSearch: string,
  intent: SearchIntent | null | undefined,
  expansion: DirectorySearchExpansion
): string[] {
  const q = normalizeSearch(rawSearch);
  const name = normalizeSearch(business.businessName || business.name || "");
  const category = normalizeSearch(
    business.categoryName || business.category || ""
  );
  const subcategory = normalizeSearch(business.subcategoryName || "");
  const locationText = [
    business.areaName,
    business.cityName,
    business.countyName,
  ]
    .map((value) => normalizeSearch(value || ""))
    .filter(Boolean)
    .join(" ");
  const itemServiceText = [
    business.shortDescription,
    business.searchText,
    business.searchableText,
  ]
    .map((value) => normalizeSearch(value || ""))
    .filter(Boolean)
    .join(" ");
  const categoryText = [category, subcategory].filter(Boolean).join(" ");
  const haystack = buildHaystack(business);
  const haystackWords = buildHaystackWordSet(business);
  const reasons: string[] = [];

  if (name === q) reasons.push("business name exact match");
  else if (name.startsWith(q)) reasons.push("business name prefix match");
  else if (name.includes(q)) reasons.push("business name text match");

  if (categoryText.includes(q)) reasons.push("category/subcategory match");
  if (itemServiceText.includes(q)) reasons.push("description or item text match");

  for (const term of expansion.phraseAliases.slice(0, 8)) {
    if (textMatchesTerm(itemServiceText, buildExpandedWordSet(tokenizePhrase(itemServiceText)), term)) {
      reasons.push(`service/item phrase alias: ${term}`);
    } else if (textMatchesTerm(categoryText, buildExpandedWordSet(tokenizePhrase(categoryText)), term)) {
      reasons.push(`category phrase alias: ${term}`);
    } else if (textMatchesTerm(name, buildExpandedWordSet(tokenizePhrase(name)), term)) {
      reasons.push(`name phrase alias: ${term}`);
    } else if (textMatchesTerm(haystack, haystackWords, term)) {
      reasons.push(`fallback phrase alias: ${term}`);
    }

    if (reasons.length >= 5) break;
  }

  for (const term of expansion.candidateTerms.slice(0, 12)) {
    if (textMatchesTerm(itemServiceText, buildExpandedWordSet(tokenizePhrase(itemServiceText)), term)) {
      reasons.push(`service/item candidate: ${term}`);
    } else if (textMatchesTerm(categoryText, buildExpandedWordSet(tokenizePhrase(categoryText)), term)) {
      reasons.push(`category candidate: ${term}`);
    } else if (textMatchesTerm(name, buildExpandedWordSet(tokenizePhrase(name)), term)) {
      reasons.push(`name candidate: ${term}`);
    } else if (textMatchesTerm(haystack, haystackWords, term)) {
      reasons.push(`fallback candidate: ${term}`);
    }

    if (reasons.length >= 5) break;
  }

  for (const term of expansion.boostTerms.slice(0, 8)) {
    if (textMatchesTerm(haystack, haystackWords, term)) {
      reasons.push(`boost-only alias: ${term}`);
    }

    if (reasons.length >= 6) break;
  }

  for (const term of expansion.locationTerms.slice(0, 6)) {
    if (textMatchesTerm(locationText, buildExpandedWordSet(tokenizePhrase(locationText)), term)) {
      reasons.push(`location boost: ${term}`);
    }

    if (reasons.length >= 6) break;
  }

  for (const term of expansion.locationAliases.slice(0, 4)) {
    if (textMatchesTerm(locationText, buildExpandedWordSet(tokenizePhrase(locationText)), term)) {
      reasons.push(`location alias boost: ${term}`);
    }

    if (reasons.length >= 6) break;
  }

  if (intent?.openNow && business.isOpenNow === true) {
    reasons.push("open-now boost");
  }

  if (business.isFeatured) reasons.push("featured boost");
  if (business.isVerified) reasons.push("verified boost");

  return uniqueSearchStrings(reasons, 6);
}

function logDirectorySearchDebug(options: {
  originalQuery: string;
  ranked: DirectoryBusiness[];
  candidateCount: number;
  expansion: DirectorySearchExpansion;
  intent?: SearchIntent | null;
}) {
  if (!__DEV__) return;

  const q = normalizeSearch(options.originalQuery);
  if (!q) return;

  console.log("Directory search ranking completed.", {
    candidateCount: options.candidateCount,
    rankedCount: options.ranked.length,
    topRankingMetrics: options.ranked.slice(0, 5).map((business) => ({
      score: scoreBusiness(business, q, options.intent, options.expansion),
      reasonCount: getTopRankingReasons(
        business,
        q,
        options.intent,
        options.expansion
      ).length,
    })),
  });
}

function buildCommonConstraints(options: {
  cityId?: string | null;
  areaId?: string | null;
}): QueryConstraint[] {
  const constraints: QueryConstraint[] = [
    where("status", "==", "approved"),
    where("isPublished", "==", true),
    where("isDeleted", "==", false),

    /**
     * Important:
     * This must match firestore.rules isPublicVisibleSearchDoc().
     * Without this, public and owner users can get permission-denied.
     */
    where("isDeactivated", "==", false),
  ];

  if (options.cityId) {
    constraints.push(where("cityId", "==", options.cityId));
  }

  if (options.areaId) {
    constraints.push(where("areaId", "==", options.areaId));
  }

  return constraints;
}

function buildBaseQuery(options: {
  cityId?: string | null;
  areaId?: string | null;
  limitCount: number;
}): Query {
  const ref = collection(db, "businessSearch");

  return query(
    ref,
    ...buildCommonConstraints(options),
    orderBy("businessNameLower", "asc"),
    limit(options.limitCount)
  );
}

function buildQueryPrefixes(
  search: string,
  expansion?: DirectorySearchExpansion
): string[] {
  const normalized = normalizeSearch(search);
  const tokens = tokenizeSearchTerms(normalized);

  const sourceTerms = uniqueSearchStrings([
    normalized,
    ...tokens,
    ...(expansion?.prefixTerms ?? []),
    ...(expansion?.candidateTerms ?? []),
    ...(expansion?.phraseAliases ?? []),
    ...(expansion?.anchorTerms ?? []),
    ...(expansion?.locationTerms ?? []),
  ]);

  return sourceTerms
    .flatMap((term) => {
      const termTokens = tokenizeSearchTerms(term);
      const words = termTokens.length > 0 ? termTokens : [term];

      return words.flatMap((word) => [word, ...getSearchAliases(word)]);
    })
    .map((prefix) => normalizeSearch(prefix || ""))
    .filter(Boolean)
    .filter((prefix) => !prefix.includes(" "))
    .filter((prefix, index, arr) => arr.indexOf(prefix) === index)
    .slice(0, QUERY_PREFIX_LIMIT);
}

function buildSearchQuery(options: {
  search: string;
  cityId?: string | null;
  areaId?: string | null;
  limitCount: number;
  expansion?: DirectorySearchExpansion;
}): Query {
  const ref = collection(db, "businessSearch");
  const prefixes = buildQueryPrefixes(options.search, options.expansion);
  const searchConstraint =
    prefixes.length > 1
      ? where("searchPrefixes", "array-contains-any", prefixes)
      : where("searchPrefixes", "array-contains", prefixes[0] || options.search);

  return query(
    ref,
    ...buildCommonConstraints(options),
    searchConstraint,
    orderBy("businessNameLower", "asc"),
    limit(options.limitCount)
  );
}

export function listenDirectoryBusinesses(
  options: SearchOptions,
  onData: (items: DirectoryBusiness[]) => void
): Unsubscribe {
  const search = normalizeSearch(options.search || "");
  const originalSearch =
    options.intent?.originalQuery || options.search || search;
  const expansion = search
    ? buildDirectorySearchExpansion(originalSearch, options.intent)
    : null;
  const cityId = options.cityId ?? null;
  const areaId = options.areaId ?? null;
  const limitCount = options.limitCount ?? 30;
  const searchFetchLimit = Math.max(
    limitCount * SEARCH_FETCH_MULTIPLIER,
    MIN_SEARCH_FETCH_LIMIT
  );

  const qy = search
    ? buildSearchQuery({
        search,
        cityId,
        areaId,
        limitCount: searchFetchLimit,
        expansion: expansion ?? undefined,
      })
    : buildBaseQuery({
        cityId,
        areaId,
        limitCount,
      });

  return onSnapshot(
    qy,
    (snap) => {
      const rows = snap.docs.map((docSnap) =>
        mapDoc(docSnap.data() as Record<string, unknown>, docSnap.id)
      );

      const rankedResults =
        search && expansion
          ? rankResults(rows, search, options.intent, expansion)
          : { items: rows, candidateCount: rows.length };

      const ranked = rankedResults.items.slice(0, limitCount);

      if (search && expansion) {
        logDirectorySearchDebug({
          originalQuery: originalSearch,
          ranked,
          candidateCount: rankedResults.candidateCount,
          expansion,
          intent: options.intent,
        });
      }

      onData(ranked);
    },
    (error) => {
      const code = String(error?.code ?? "").trim().slice(0, 80);
      console.warn(
        "Directory search snapshot failed.",
        code ? { code } : undefined
      );

      onData([]);
    }
  );
}
